import { useMemo } from 'react';
import { radii, r, s, spacing } from '@/theme/tokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { useTheme } from './hooks';
import { Skeleton } from './Skeleton';

type StackedBarChartSkeletonProps = {
  height?: number;
  plotAreaHeight?: number;
  bars?: number;
  showPlotAreaBackground?: boolean;
  hideYAxisLine?: boolean;
  barWidthRatio?: number;
  barCornerRadius?: number;
  categoryGapRatio?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  yAxisWidth?: number;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const toPx = (value: number): string => `${value}px`;

export const StackedBarChartSkeleton = ({
  height = 200,
  plotAreaHeight,
  bars = 7,
  showPlotAreaBackground = true,
  hideYAxisLine = false,
  barWidthRatio = 0.48,
  barCornerRadius = radii.md,
  categoryGapRatio,
  marginLeft = -8,
  marginRight = 32,
  marginTop = spacing['2xl'] + spacing.sm,
  marginBottom = spacing['3xl'] + spacing.xs,
  yAxisWidth = 52,
}: StackedBarChartSkeletonProps): JSX.Element => {
  const resolvedCategoryGapRatio = categoryGapRatio ?? chartBarSizing.default.categoryGapRatio;
  const theme = useTheme();
  const chartInnerPaddingX = s('md');
  const plotLeftPx = yAxisWidth + marginLeft;
  const barHeightBasis = plotAreaHeight ?? height - marginTop - marginBottom;
  const clampedBarHeightBasis = Math.max(0, barHeightBasis);
  const resolvedContainerHeight =
    plotAreaHeight === undefined ? height : toPx(plotAreaHeight + marginTop + marginBottom);
  const axisLeft = toPx(Math.max(0, plotLeftPx));
  const chartTop = toPx(marginTop);
  const chartRight = toPx(marginRight);
  const axisBottom = toPx(marginBottom);
  const plotAreaBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const plotFrameStrokeWidth = '0.5px';
  const barTopRadius = toPx(barCornerRadius);
  const barHeights = useMemo(() => {
    const resolvedBars = clamp(bars, 4, 18);
    return Array.from({ length: resolvedBars }, (_, index) => {
      const offset = Math.abs((index % 5) - 2);
      const multiplier = 0.4 + offset * 0.12;
      return clamp(
        clampedBarHeightBasis * multiplier,
        clampedBarHeightBasis * 0.35,
        clampedBarHeightBasis * 0.9,
      );
    });
  }, [bars, clampedBarHeightBasis]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: resolvedContainerHeight }}>
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          right: chartRight,
          bottom: axisBottom,
          top: chartTop,
          backgroundColor: showPlotAreaBackground ? plotAreaBackgroundColor : 'transparent',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'flex-end',
          gap: `calc(${s('xl')} * ${
            resolvedCategoryGapRatio / chartBarSizing.default.categoryGapRatio
          })`,
          paddingLeft: chartInnerPaddingX,
          paddingRight: chartInnerPaddingX,
          boxShadow: [
            `inset ${plotFrameStrokeWidth} 0 0 ${theme.app.colors.divider}`,
            `inset -${plotFrameStrokeWidth} 0 0 ${theme.app.colors.divider}`,
            `inset 0 ${plotFrameStrokeWidth} 0 ${theme.app.colors.divider}`,
            `inset 0 -${plotFrameStrokeWidth} 0 ${theme.app.colors.divider}`,
          ].join(', '),
          opacity: hideYAxisLine ? 1 : 0.7,
        }}
      >
        {[0.25, 0.5, 0.75].map((offset) => (
          <Box
            key={`stacked-chart-skeleton-guide-${offset}`}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${offset * 100}%`,
              height: '1px',
              backgroundColor: theme.app.colors.divider,
              opacity: 0.4,
            }}
          />
        ))}
        {barHeights.map((barHeight, index) => (
          <Box
            key={`bar-${index}`}
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: s('xxxs'),
              flex: 1,
              minWidth: 0,
            }}
          >
            <Skeleton
              variant="rectangular"
              height={barHeight}
              sx={{
                width: `${clamp(barWidthRatio, 0.25, 0.8) * 100}%`,
                maxWidth: '132px',
                mx: 'auto',
                borderRadius: `${barTopRadius} ${barTopRadius} ${r('none')} ${r('none')}`,
                bgcolor: theme.app.colors.border,
                opacity: 0.6,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
